import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, UserPlus, Trash2, Shield, User as UserIcon, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { useAppSelector, useAppDispatch } from '../../redux/store';
import { updateMember, removeMember as removeMemberAction } from '../../redux/slices/organizationSlice';
import organizationService from '../../services/organization.service';
import { canManageMembers } from '../../utils/organizationPermissions';
import InviteMemberDialog from './InviteMemberDialog';
import RemoveMemberDialog from './RemoveMemberDialog';

interface MemberManagementProps {
    currentUser: any;
    onUpdate: () => void;
}

/**
 * Member Management Component
 * Displays organization members and allows admins to manage roles and invitations
 */
const MemberManagement: React.FC<MemberManagementProps> = ({ currentUser, onUpdate }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const members = useAppSelector(state => state.organization.members);
    const loading = useAppSelector(state => state.organization.loading);

    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState<any>(null);
    const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
    const [resendingInvite, setResendingInvite] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // IMPORTANT: Get the current user's role from the members list, not from currentUser.organizationRole
    // currentUser.organizationRole might be from a different organization
    const currentUserMember = members.find(m => m._id === currentUser?._id);
    const currentUserRole = currentUserMember?.organizationRole || 'member';
    const canManage = currentUserRole === 'admin' || currentUserRole === 'owner';


    const handleRoleChange = async (memberId: string, newRole: 'admin' | 'member') => {
        try {
            setUpdatingMemberId(memberId);
            setError(null);

            const updated = await organizationService.updateMemberRole(memberId, newRole);
            dispatch(updateMember(updated));
        } catch (err: any) {
            console.error('Failed to update member role:', err);
            setError(err.response?.data?.error || err.message || t('organization.members.updateRoleFailed'));
        } finally {
            setUpdatingMemberId(null);
        }
    };

    const handleRemoveMember = (member: any) => {
        setSelectedMember(member);
        setRemoveDialogOpen(true);
    };

    const confirmRemoveMember = async () => {
        if (!selectedMember) return;

        try {
            await organizationService.removeMember(selectedMember._id);
            dispatch(removeMemberAction(selectedMember._id));
            setRemoveDialogOpen(false);
            setSelectedMember(null);
            onUpdate();
        } catch (err: any) {
            console.error('Failed to remove member:', err);
            setError(err.response?.data?.error || err.message || t('organization.members.removeFailed'));
        }
    };

    const handleResendInvite = async (member: any) => {
        try {
            setResendingInvite(member._id);
            setError(null);
            setSuccessMessage(null);

            await organizationService.inviteMember(member.email);
            setSuccessMessage(t('organization.members.inviteResent', `Invitation resent to ${member.email}`));

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
            console.error('Failed to resend invite:', err);
            setError(err.response?.data?.error || err.message || t('organization.members.resendFailed'));
        } finally {
            setResendingInvite(null);
        }
    };

    const getUserInitials = (member: any) => {
        if (member.displayName) {
            return member.displayName
                .split(' ')
                .map((n: string) => n[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        }
        return member.email[0].toUpperCase();
    };

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <Users className="h-5 w-5" />
                                {t('organization.members.title')}
                            </CardTitle>
                            <CardDescription>{t('organization.members.description')}</CardDescription>
                        </div>
                        {canManage && (
                            <Button onClick={() => setInviteDialogOpen(true)}>
                                <UserPlus className="mr-2 h-4 w-4" />
                                {t('organization.members.invite')}
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert className="mb-4 bg-green-50 border-green-200">
                            <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
                        </Alert>
                    )}

                    {loading && members.length === 0 ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold mx-auto mb-2"></div>
                            <p className="text-sm text-muted-foreground">{t('common.labels.loading')}</p>
                        </div>
                    ) : members.length === 0 ? (
                        <div className="text-center py-8">
                            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">{t('organization.members.noMembers')}</p>
                        </div>
                    ) : (
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('organization.members.tableHeaders.name')}</TableHead>
                                        <TableHead>{t('organization.members.tableHeaders.email')}</TableHead>
                                        <TableHead>{t('organization.members.tableHeaders.role')}</TableHead>
                                        {canManage && <TableHead className="text-right">{t('organization.members.tableHeaders.actions')}</TableHead>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {members.map((member) => {
                                        const isCurrentUser = member._id === currentUser?._id;
                                        const isUpdating = updatingMemberId === member._id;

                                        return (
                                            <TableRow key={member._id}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <Avatar className="h-8 w-8">
                                                            <AvatarImage src={member.profilePicture} alt={member.displayName || member.email} />
                                                            <AvatarFallback className="bg-gold/10 text-gold text-xs">
                                                                {getUserInitials(member)}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div>
                                                            <div className="font-medium flex items-center gap-2">
                                                                {member.displayName || member.email.split('@')[0]}
                                                                {isCurrentUser && (
                                                                    <Badge variant="secondary" className="ml-2 text-xs">
                                                                        {t('organization.members.you')}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">{member.email}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {canManage && !isCurrentUser && member.organizationRole !== 'owner' ? (
                                                            <Select
                                                                value={member.organizationRole || 'member'}
                                                                onValueChange={(value) => handleRoleChange(member._id, value as 'admin' | 'member')}
                                                                disabled={isUpdating}
                                                            >
                                                                <SelectTrigger className="w-[130px]">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="admin">
                                                                        <div className="flex items-center gap-2">
                                                                            <Shield className="h-3 w-3" />
                                                                            {t('organization.members.roles.admin')}
                                                                        </div>
                                                                    </SelectItem>
                                                                    <SelectItem value="member">
                                                                        <div className="flex items-center gap-2">
                                                                            <UserIcon className="h-3 w-3" />
                                                                            {t('organization.members.roles.member')}
                                                                        </div>
                                                                    </SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        ) : (
                                                            <Badge
                                                                variant={
                                                                    member.organizationRole === 'owner' ? 'default' :
                                                                        member.organizationRole === 'admin' ? 'default' :
                                                                            'secondary'
                                                                }
                                                                className={member.organizationRole === 'owner' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                                                            >
                                                                {member.organizationRole === 'owner' ? (
                                                                    <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                                    </svg>
                                                                ) : member.organizationRole === 'admin' ? (
                                                                    <Shield className="mr-1 h-3 w-3" />
                                                                ) : (
                                                                    <UserIcon className="mr-1 h-3 w-3" />
                                                                )}
                                                                {t(`organization.members.roles.${member.organizationRole || 'member'}`)}
                                                            </Badge>
                                                        )}
                                                        {(member.status === 'pending' || (member.status as string) === 'invited') && (
                                                            <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">
                                                                {t('organization.members.pending')}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                {canManage && (
                                                    <TableCell className="text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            {(member.status === 'pending' || (member.status as string) === 'invited') && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleResendInvite(member)}
                                                                    disabled={resendingInvite === member._id}
                                                                    className="text-gold hover:text-gold hover:bg-gold/10"
                                                                    title={t('organization.members.resendInvite', 'Resend invitation')}
                                                                >
                                                                    <Mail className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                            {!isCurrentUser && member.organizationRole !== 'owner' && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveMember(member)}
                                                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Invite Member Dialog */}
            <InviteMemberDialog
                open={inviteDialogOpen}
                onOpenChange={setInviteDialogOpen}
                onSuccess={onUpdate}
            />

            {/* Remove Member Dialog */}
            <RemoveMemberDialog
                open={removeDialogOpen}
                onOpenChange={setRemoveDialogOpen}
                member={selectedMember}
                onConfirm={confirmRemoveMember}
            />
        </>
    );
};

export default MemberManagement;
